import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ban,
  Bookmark,
  ChevronRight,
  CircleHelp,
  Clock,
  CreditCard,
  Crown,
  Eye,
  FileText,
  Lock,
  LogOut,
  Pencil,
  ShieldCheck,
  Trash2,
  User,
  X,
  XCircle,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import Navbar from "../Landing/Navbar";
import useAuthAccess, { invalidateAuthCache } from "../hooks/useAuthAccess";
import { getProfileImages } from "../utils/profileLookup";
import { getProfileDisplayImage } from "../utils/profilePlaceholder";
import { isPremiumActive, formatMembershipDate, parseMembershipDate } from "../utils/membership";

const Settings = () => {
  const navigate = useNavigate();
  const { user, profile: profileFromHook, loading } = useAuthAccess();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [loading, user, navigate]);

  const profile = useMemo(
    () => (profileFromHook ? { ...profileFromHook, imageUrls: getProfileImages(profileFromHook) } : null),
    [profileFromHook]
  );

  const logout = async () => {
    try {
      invalidateAuthCache();
      await signOut(auth);
      localStorage.removeItem("karankiAuth");
      localStorage.removeItem("karankiProfilePhone");
      navigate("/login");
    } catch (err) { console.log(err); }
  };

  const sections = [
    {
      title: "Account",
      items: [
        { title: "My Account", desc: "Your complete profile information", Icon: User, to: "/settings/account" },
        { title: "Edit Profile", desc: "Photos, bio, preferences & details", Icon: Pencil, to: "/my-profile" },
        { title: "Privacy", desc: "Who can see your profile", Icon: Lock, to: "/settings/privacy" },
        { title: "Delete Account", desc: "Permanently remove your account", Icon: Trash2, to: "/settings/delete-account", danger: true },
      ],
    },
    {
      title: "Activity",
      items: [
        { title: "Who Viewed Me", desc: "See who's interested", Icon: Eye, to: "/settings/views", premium: true },
        { title: "Blocked Profiles", desc: "Profiles you blocked", Icon: Ban, to: "/settings/blocked" },
        { title: "Shortlisted", desc: "Profiles you saved", Icon: Bookmark, to: "/settings/shortlisted" },
      ],
    },
    {
      title: "Membership & billing",
      items: [
        { title: "Membership", desc: "Plan & upgrade", Icon: Crown, to: "/settings/membership", premium: true },
        { title: "Payment History", desc: "Receipts & invoices", Icon: CreditCard, to: "/settings/payments" },
      ],
    },
    {
      title: "Legal",
      items: [
        { title: "Privacy Policy", desc: "How we collect and use your information", Icon: ShieldCheck, to: "/privacy-policy" },
        { title: "Terms & Conditions", desc: "Terms for using Karanki", Icon: FileText, to: "/terms-conditions" },
      ],
    },
    {
      title: "Other",
      items: [
        { title: "Help & Support", desc: "Get assistance", Icon: CircleHelp, to: "/settings/support" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container max-w-5xl pt-3 pb-app-nav sm:pt-6 lg:pt-8">
        {/* Header — profile + plan merged */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border border-sand-200 bg-white p-4 shadow-sm sm:p-6"
        >
          <div className="relative flex items-center gap-3 sm:gap-4">
            {loading ? (
              <div className="skeleton h-14 w-14 rounded-2xl sm:h-16 sm:w-16" />
            ) : (
              <img
                src={getProfileDisplayImage(profile)}
                alt={profile.fullName}
                onClick={() => navigate("/settings/account")}
                className="h-14 w-14 rounded-2xl object-cover object-center ring-1 ring-sand-200 cursor-pointer sm:h-16 sm:w-16"
              />
            )}
            <div className="min-w-0 flex-1">
              {loading ? (
                <>
                  <div className="skeleton h-5 w-40" />
                  <div className="mt-2 skeleton h-3 w-28" />
                </>
              ) : (
                <>
                  <h2
                    onClick={() => navigate("/settings/account")}
                    className="cursor-pointer font-display text-base font-bold leading-tight text-ink-800 transition hover:text-brand-600 sm:text-xl"
                  >
                    {profile?.fullName || user?.displayName || "Karanki User"}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {profile?.kmProfileId && (
                      <span className="inline-flex items-center rounded-full bg-sand-50 px-2.5 py-1 text-[10px] font-semibold text-ink-600">
                        Profile ID: {profile.kmProfileId}
                      </span>
                    )}
                    {profile?.profileStatus === "Verified" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
                        <ShieldCheck size={12} /> Verified
                      </span>
                    )}
                    {profile?.profileStatus === "Pending Verification" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold text-amber-700">
                        <Clock size={11} /> Pending
                      </span>
                    )}
                    {profile?.profileStatus === "Rejected" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10.5px] font-bold text-rose-700">
                        <XCircle size={11} /> Rejected
                      </span>
                    )}
                    {profile?.profileStatus === "Changes Requested" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10.5px] font-bold text-violet-700">
                        <Pencil size={11} /> Changes needed
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => navigate("/my-profile")}
              className="hidden btn-ghost sm:inline-flex"
            >
              Edit profile
            </button>
          </div>

          {/* Plan status row */}
          {!loading && profile && (
            <button
              onClick={() => navigate("/settings/membership")}
              className={`relative mt-4 flex w-full items-center gap-3 border-t border-sand-200 px-1 pt-4 text-left transition sm:mt-5 sm:gap-3.5 sm:pt-5 ${
                isPremiumActive(profile)
                  ? "hover:text-amber-800"
                  : "hover:text-brand-700"
              }`}
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                isPremiumActive(profile) ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand-600"
              }`}>
                <Crown size={17} />
              </div>
              <div className="min-w-0 flex-1">
                {isPremiumActive(profile) ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <p className="text-[13px] font-semibold text-ink-800">Premium plan</p>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-800">Active</span>
                    </div>
                    <p className="mt-0.5 text-[11.5px] text-ink-500">
                      Valid until {formatMembershipDate(parseMembershipDate(profile.membershipExpiresAt))}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <p className="text-[13px] font-semibold leading-5 text-ink-800">Free plan</p>
                    </div>
                    <p className="text-[11.5px] leading-4 text-ink-500">Limited access</p>
                  </>
                )}
              </div>
              {isPremiumActive(profile) ? (
                <ChevronRight size={15} className="text-amber-400" />
              ) : (
                <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-brand-700">
                  View plans <ChevronRight size={15} />
                </span>
              )}
            </button>
          )}
        </motion.section>

        {/* Sections */}
        <div className="mt-6 space-y-7 sm:mt-8 sm:space-y-8">
          {sections.map((section) => (
            <section key={section.title}>
              <div className="flex items-center justify-between border-b border-sand-200 px-1 pb-2">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-400">{section.title}</h2>
              </div>
              <div>
                {section.items.map((item) => {
                  const Icon = item.Icon;
                  return (
                    <button
                      key={item.title}
                      onClick={() => navigate(item.to)}
                      className={`group flex min-h-[68px] w-full items-center gap-3 border-b border-sand-100 px-1 py-3 text-left transition-colors hover:bg-sand-50/60 sm:gap-4 sm:py-3.5 ${
                        item.danger ? "hover:bg-rose-50/60" : ""
                      }`}
                    >
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10 ${
                          item.danger ? "bg-rose-50 text-rose-500" : item.premium ? "bg-amber-50 text-amber-600" : "bg-brand-50 text-brand-600"
                        }`}
                      >
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={`text-[13px] font-semibold leading-5 ${item.danger ? "text-rose-600" : "text-ink-800"}`}>{item.title}</h3>
                        <p className={`mt-0.5 text-[11px] leading-4 sm:text-[12px] ${item.danger ? "text-rose-400" : "text-ink-400"}`}>{item.desc}</p>
                      </div>
                      <ChevronRight size={16} className={`shrink-0 transition group-hover:translate-x-0.5 ${item.danger ? "text-rose-300 group-hover:text-rose-500" : "text-ink-300 group-hover:text-brand-600"}`} />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          {/* Logout */}
          <button
            onClick={() => setShowLogoutModal(true)}
            className="group flex min-h-[68px] w-full items-center gap-3 border-b border-sand-100 px-1 py-3 text-left transition-colors hover:bg-rose-50/60 sm:gap-4 sm:py-3.5"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50 text-rose-500 sm:h-10 sm:w-10">
              <LogOut size={16} />
            </span>
            <span className="flex-1">
              <span className="block text-[13px] font-semibold text-rose-600">Logout</span>
              <span className="mt-0.5 block text-[11px] leading-4 text-rose-400 sm:text-[12px]">Sign out from this device</span>
            </span>
            <ChevronRight size={16} className="text-rose-300 transition group-hover:translate-x-0.5 group-hover:text-rose-500" />
          </button>

          <p className="pt-4 text-center text-[11px] text-ink-300">
            Karanki Matrimony • v1.0
          </p>
        </div>
      </main>

      {/* Logout Modal */}
      <AnimatePresence>
        {showLogoutModal && (
          <ConfirmModal
            title="Logout"
            description="Are you sure you want to logout from your account?"
            Icon={LogOut}
            confirmLabel="Logout"
            tone="rose"
            onClose={() => setShowLogoutModal(false)}
            onConfirm={logout}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ConfirmModal = ({ title, description, Icon, confirmLabel, onClose, onConfirm, tone = "rose" }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
    className="fixed inset-0 z-[60] flex items-end justify-center bg-ink-900/45 px-4 backdrop-blur-sm sm:items-center"
  >
    <motion.div
      initial={{ y: 30, opacity: 0, scale: 0.97 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: 30, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
      className="card-elevated w-full max-w-md overflow-hidden rounded-t-[28px] sm:rounded-3xl"
    >
      <div className="flex items-start gap-4 px-6 pt-6">
        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-${tone}-50 text-${tone}-600`}>
          <Icon size={24} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl font-bold text-ink-800">{title}</h3>
          <p className="mt-1.5 text-[13.5px] leading-6 text-ink-500">{description}</p>
        </div>
        <button onClick={onClose} className="btn-icon h-9 w-9">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 px-6 py-5">
        <button onClick={onClose} className="btn-ghost py-3">Cancel</button>
        <button onClick={onConfirm} className="btn-primary py-3">
          {confirmLabel}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

export default Settings;
